"use client";

import { Chip } from "@cmp-src/data-display/chips/Chip/Chip";
import { UserChip } from "@cmp-src/data-display/chips/UserChip/UserChip";
import { IconTile } from "@cmp-src/data-display/IconTile";
import type {
  IUserFull,
  WorkplaceRequestReservationAttendeeInput,
  WorkplaceRequestReservationFieldValue,
  WorkplaceRequestReservationRunResult
} from "@gettactic/api";
import { dateParseISO } from "@gettactic/helpers/src/date-time/dateParseISO";
import { time24To12 } from "@gettactic/helpers/src/date-time/timeStringConverters";
import { timeZoneLabel } from "@gettactic/helpers/src/date-time/timeZoneLabel";
import { reservationDurationLabel } from "@gettactic/helpers/src/workplace-requests/reservationFieldValue";
import { useOfficesQuery, useUserListByIds } from "@gettactic/hooks";
import { format } from "date-fns";
import { CalendarClock } from "lucide-react";
import React, { useMemo } from "react";
import { describeReservationRRule } from "@/features/workplace-requests/lib/reservationRecurrence";

/**
 * Past this many the block stops being a summary and starts being a list, so
 * the rest is counted instead. The whole answer is one click away under Edit.
 */
const MAX_SHOWN_DAYS = 6;
const MAX_SHOWN_ATTENDEES = 6;

interface BookedAttendeesProps {
  attendees: WorkplaceRequestReservationAttendeeInput[];
}

/**
 * The guest list the booking was made with. The run records people by id
 * only, so their names are looked up here; a guest is its own name.
 */
const BookedAttendees: React.FC<BookedAttendeesProps> = ({ attendees }) => {
  const userIds = useMemo(
    () =>
      attendees
        .map((attendee) => attendee.user_id)
        .filter((userId): userId is string => !!userId),
    [attendees]
  );
  const users = useUserListByIds(userIds);
  const usersById = useMemo(() => {
    const byId = new Map<string, IUserFull>();
    for (const user of (users.data ?? []).filter(Boolean) as IUserFull[]) {
      byId.set(user.id, user);
    }
    return byId;
  }, [users.data]);

  const shown = attendees.slice(0, MAX_SHOWN_ATTENDEES);
  const hidden = attendees.length - shown.length;

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {shown.map((attendee) => {
        if (attendee.user_id) {
          // Held back until the names are in: a chip is only worth showing
          // once it can say who it stands for.
          if (users.isLoading) return null;
          return (
            <UserChip
              key={attendee.user_id}
              user={usersById.get(attendee.user_id) ?? { name: "Unknown user" }}
            />
          );
        }
        return <Chip key={attendee.email}>{attendee.email}</Chip>;
      })}
      {hidden > 0 ? (
        <span className="text-neutral-500 text-xs">+{hidden} more</span>
      ) : null}
    </div>
  );
};

interface ReservationValueCardProps {
  value: WorkplaceRequestReservationFieldValue;
  /** The request's office: a reservation's times are that office's wall-clock. */
  officeId: string;
  /**
   * What the approval booked, once it has. The answer names the attendees the
   * requester asked for; the booking adds the workflow's default attendees and
   * the approver's edits, so once it exists it is the list to show.
   */
  booked?: WorkplaceRequestReservationRunResult | null;
}

export const ReservationValueCard: React.FC<ReservationValueCardProps> = ({
  value,
  officeId,
  booked
}) => {
  const officesQuery = useOfficesQuery();
  const timeZone = officesQuery.data?.offices.find(
    (office) => office.id === officeId
  )?.time_zone;

  const bookedAttendees = booked?.attendees ?? null;
  const attendees = value.attendees ?? [];
  const attendeeCount = bookedAttendees?.length ?? attendees.length;
  const window = `${time24To12(value.start_time)} – ${time24To12(value.end_time)}`;

  // One day names itself; a run of them is counted, because the headline has
  // to stay one line and nine dates never will.
  const headline =
    value.dates.length === 1
      ? `${format(dateParseISO(value.dates[0]), "EEE, d MMM")} · ${window}`
      : `${value.dates.length} days · ${window}`;

  const summary = [
    reservationDurationLabel(value),
    value.recurrence
      ? describeReservationRRule(value.recurrence.rrule)
      : "Does not repeat",
    attendeeCount > 0
      ? `${attendeeCount} ${attendeeCount === 1 ? "attendee" : "attendees"}`
      : null,
    timeZone ? timeZoneLabel(timeZone) : null
  ]
    .filter(Boolean)
    .join(" · ");

  const shownDays = value.dates.slice(0, MAX_SHOWN_DAYS);
  const hiddenDays = value.dates.length - shownDays.length;
  const shownAttendees = attendees.slice(0, MAX_SHOWN_ATTENDEES);
  const hiddenAttendees = attendees.length - shownAttendees.length;

  return (
    <div className="flex flex-col gap-3 rounded-lg border border-neutral-200 bg-neutral-50/60 p-3">
      <div className="flex items-start gap-3">
        <IconTile color="#EEF0FF" size="md">
          <CalendarClock className="h-4 w-4" />
        </IconTile>
        <div className="flex min-w-0 flex-col">
          <span className="font-medium text-neutral-900">{headline}</span>
          <span className="text-neutral-500 text-xs">{summary}</span>
        </div>
      </div>

      {/*
       * The days and the guests are the detail behind the headline, so they
       * sit under a rule rather than running straight on from it. Absent both,
       * there is nothing to divide and no rule is drawn.
       */}
      {value.dates.length > 1 || attendeeCount > 0 ? (
        <div className="flex flex-col gap-2 border-separator border-t pt-3">
          {value.dates.length > 1 ? (
            <p className="text-neutral-600 text-xs">
              {shownDays
                .map((date) => format(dateParseISO(date), "d MMM"))
                .join(", ")}
              {hiddenDays > 0 ? ` +${hiddenDays} more` : ""}
            </p>
          ) : null}

          {bookedAttendees && bookedAttendees.length > 0 ? (
            <BookedAttendees attendees={bookedAttendees} />
          ) : null}

          {!bookedAttendees && attendees.length > 0 ? (
            <div className="flex flex-wrap items-center gap-1.5">
              {shownAttendees.map((attendee) =>
                attendee.user ? (
                  <UserChip
                    key={attendee.user_id ?? attendee.email}
                    // Only what the chip draws: the request's user shape carries
                    // roles as plain strings, which the fuller one doesn't accept.
                    user={{
                      name: attendee.user.name,
                      profile_photo_url: attendee.user.profile_photo_url
                    }}
                  />
                ) : (
                  // A guest, or somebody who has since left: the address is all
                  // there is to show.
                  <Chip key={attendee.user_id ?? attendee.email}>
                    {attendee.email ?? "Unknown user"}
                  </Chip>
                )
              )}
              {hiddenAttendees > 0 ? (
                <span className="text-neutral-500 text-xs">
                  +{hiddenAttendees} more
                </span>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}

      <p className="text-neutral-500 text-xs">
        {booked
          ? "Booked when this request was approved. The attendees above are the ones invited."
          : "Created on the room when this request is approved. Attendees are invited at the same moment."}
      </p>
    </div>
  );
};
